/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: prop-types library.
import _ from 'underscore' // Npm: utility module.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useQuery } from '@apollo/client' // Npm: Apollo client.
import { Chip } from '@mui/material' // Npm: Material UI components.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import { HiArrowPath, HiArrowUturnDown } from 'react-icons/hi2' // Npm: react-icons library.
import {
  Button,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DateRangePicker from 'components/DateRangePicker'
import TableSpinner from 'components/TableSpinner'
import { MemoizedSelect } from 'components/MemoizedInput'
import MccSelector from 'components/MccSelector'


/*
 * GRAPHS
 */
import CustomerRouteTrafficQuery from './__query__/index.sms.read.query'
import CustomerAccountReadQuery from './__query__/index.customerAccount.read.query'



const _toNumber = value => Number(value ?? 0) || 0
const _extractMcc = value => value?.match?.(/\((\d+)\)(?!.*\d)/)?.[1] ?? ''
const _extractCountryName = value => value?.replace?.(/\s*\([^)]*\)\s*$/, '') ?? ''


/*
 * OBJECTS
 */
const Index = ({ passOn, activeCustomer, customerOverride, isEmbedded = false }) => {
  const _customerId = customerOverride?.customerId || (!isEmbedded ? passOn?.customerId || activeCustomer?.customerId : void 0)
  const _companyName = customerOverride?.companyName || (!isEmbedded ? passOn?.companyName || activeCustomer?.companyName : void 0)

  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    'selection': {
      'startDate': customerOverride?.startDate ?? passOn?.startDate ?? Object.DateRangeProvider()?.startDate,
      'endDate': customerOverride?.endDate ?? passOn?.endDate ?? Object.DateRangeProvider()?.endDate,
      'key': 'selection'
    }
  })
  const [customerAccountId, setCustomerAccountId] = React.useState(void 0)
  const [destination, setDestination] = React.useState({ 'mcc': '', 'mnc': [] })
  const [routeTrafficReport, setRouteTrafficReport] = React.useState({})
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;
 
  const [QueryRouteTraffic, QueryRouteTrafficResponse] = useLazyQuery(CustomerRouteTrafficQuery)
  const _selectedMcc = React.useMemo(() => _extractMcc(destination?.mcc), [destination?.mcc])
  const _selectedCountryName = React.useMemo(() => _extractCountryName(destination?.mcc), [destination?.mcc])
  const _QueryCustomerAccountRead = useQuery(CustomerAccountReadQuery, {
    'variables': { 'customerId': _customerId },
    'skip': _.isEmpty(_customerId),
    'fetchPolicy': Object.React.App.fetchPolicy
  })

  React.useEffect(() => {
    if (!customerOverride) return

    setDateRange({
      'selection': {
        'startDate': customerOverride?.startDate ?? Object.DateRangeProvider()?.startDate,
        'endDate': customerOverride?.endDate ?? Object.DateRangeProvider()?.endDate,
        'key': 'selection'
      }
    })
    setCustomerAccountId(void 0)
    setDestination({ 'mcc': '', 'mnc': [] })
  }, [customerOverride])

  React.useEffect(() => {
    const _Async = async () => {
      if (_.isEmpty(_customerId)) {
        setRouteTrafficReport({})
        return
      }

      const { data, error } = await QueryRouteTraffic({
        'variables': {
          'customerId': _customerId,
          'startDate': dateRange.selection.startDate,
          'endDate': dateRange.selection.endDate,
          'countryNameToSearch': _.isEmpty(_selectedMcc) ? void 0 : _selectedCountryName,
          'mccToSearch': _.isEmpty(_selectedMcc) ? void 0 : _selectedMcc
        },
        'fetchPolicy': Object.React.App.fetchPolicy
      })

      if (error instanceof Error) throw error

      setRouteTrafficReport(data?.CustomerRouteTraffic?._report ?? {})
    }

    _Async().catch(i => { throw i })
  }, [QueryRouteTraffic, _customerId, _selectedCountryName, _selectedMcc, dateRange, refreshTrigger])

  const _accountNameById = React.useMemo(() => {
    const _container = {}

    for (const item of _QueryCustomerAccountRead?.data?.CustomerAccountRead ?? []) {
      _container[item.id] = item.displayName ? `${item.displayName} (${item.id})` : item.id
    }

    return _container
  }, [_QueryCustomerAccountRead?.data?.CustomerAccountRead])

  const _customerAccountOptions = React.useMemo(() => (_QueryCustomerAccountRead?.data?.CustomerAccountRead ?? [])?.map(j => ({
    label: j.displayName ? `${j.displayName} (${j.id})` : j.id,
    value: j.id
  })), [_QueryCustomerAccountRead?.data?.CustomerAccountRead])

  const _routeTraffic = React.useMemo(() => {
    const _rows = []

    for (const [accountId, routeTypeGroup] of Object.entries(routeTrafficReport ?? {})) {
      if (!_.isEmpty(customerAccountId) && accountId !== customerAccountId) continue

      for (const [routeType, routePlanGroup] of Object.entries(routeTypeGroup ?? {})) {
        for (const [routePlanId, routePlanReport] of Object.entries(routePlanGroup ?? {})) {
          if (Object.prototype.hasOwnProperty.call(routePlanReport ?? {}, 'totalMessages')) {
            _rows.push({
              accountId,
              routeType,
              routePlanId,
              ...routePlanReport
            })

            continue
          }

          for (const [vendorSmppKey, report] of Object.entries(routePlanReport ?? {})) {
            _rows.push({
              accountId,
              routeType,
              routePlanId,
              vendorSmppKey,
              ...report
            })
          }
        }
      }
    }

    return _rows
  }, [customerAccountId, routeTrafficReport])

  const columns = React.useMemo(
    () => [
      {
        field: 'sno',
        headerName: 'S.No.',
        align: 'center',
        headerAlign: 'center',
        width: 80,
        sortable: false,
        filterable: false
      },
      {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        hide: true
      },
      {
        field: 'customerAccount',
        headerName: 'Customer Account',
        editable: false,
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        minWidth: 240,
        flex: 1.2
      },
      {
        field: 'routeType',
        headerName: 'Route Type',
        editable: false,
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        minWidth: 160,
        flex: 1,
        renderCell: ({ value }) => {
          const _key = value ?? 'unknown'

          return (
            <Chip
              label={Object.React.App.routeTraffic.labels[_key] ?? _key}
              size='small'
              sx={{
                backgroundColor: `${Object.React.App.routeTraffic.backgroundColors[_key] ?? Object.React.App.routeTraffic.backgroundColors.unknown} !important`,
                borderColor: `${Object.React.App.routeTraffic.colors[_key] ?? Object.React.App.routeTraffic.colors.unknown}33 !important`,
                color: `${Object.React.App.routeTraffic.colors[_key] ?? Object.React.App.routeTraffic.colors.unknown} !important`,
                fontWeight: 500,
                fontSize: '10px',
                height: '22px',
                border: '1px solid',
                '& .MuiChip-label': {
                  px: '7px'
                }
              }}
            />
          )
        }
      },
      {
        field: 'countryName',
        headerName: 'Country',
        editable: false,
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        minWidth: 160,
        flex: 0.9
      },
      {
        field: 'vendorSmppDisplayName',
        headerName: 'Vendor SMPP',
        editable: false,
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        minWidth: 220,
        flex: 1.1
      },
      {
        field: 'totalMessages',
        headerName: 'Messages',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'totalParts',
        headerName: 'Parts',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 110,
        flex: 0.6
      },
      {
        field: 'acceptedcount',
        headerName: 'Accepted',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'enroutecount',
        headerName: 'Enroute',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'deliveredcount',
        headerName: 'Delivered',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'undeliverablecount',
        headerName: 'Undeliverable',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 150,
        flex: 0.8
      },
      {
        field: 'failedcount',
        headerName: 'Failed',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 110,
        flex: 0.6
      },
      {
        field: 'rejectedcount',
        headerName: 'Rejected',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'expiredcount',
        headerName: 'Expired',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 120,
        flex: 0.7
      },
      {
        field: 'submittedcount',
        headerName: 'Submitted',
        editable: false,
        sortable: true,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        minWidth: 130,
        flex: 0.7
      },
    ],
    []
  )

  const rows = React.useMemo(() => _.compact(_routeTraffic?.map((item, index) => ({
    'id': `${item.accountId}-${item.routeType}-${item.routePlanId}-${item.countryName ?? 'unknown'}-${item.vendorSmppId ?? item.vendorSmppKey ?? 'unknown'}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'customerAccount': _accountNameById[item.accountId] ?? item.accountId ?? '-',
    'routeType': item.routeType ?? 'unknown',
    'countryName': item.countryName ?? '-',
    'vendorSmppDisplayName': item.vendorSmppDisplayName ?? '-',
    'totalMessages': _toNumber(item.totalMessages),
    'totalParts': _toNumber(item.totalParts),
    'acceptedcount': _toNumber(item.acceptedcount),
    'enroutecount': _toNumber(item.enroutecount),
    'deliveredcount': _toNumber(item.deliveredcount),
    'undeliverablecount': _toNumber(item.undeliverablecount),
    'failedcount': _toNumber(item.failedcount),
    'rejectedcount': _toNumber(item.rejectedcount),
    'expiredcount': _toNumber(item.expiredcount),
    'submittedcount': _toNumber(item.submittedcount),
  }))), [_routeTraffic, paginationModel.page, paginationModel.pageSize, _accountNameById])

  const _content = (
    <>
      <Flex
        pb='0px'
        px={isEmbedded ? { base: '12px', md: '16px' } : 0}
        py={isEmbedded ? { base: '12px', md: '16px' } : 0}
        justifyContent='space-between'
        alignItems={{ base: 'stretch', lg: 'center' }}
        flexDir={{ base: 'column', lg: 'row' }}
        gap={{ base: '16px', lg: '20px' }}>
        <Flex
          flex={{ base: '1', lg: '0 1 auto' }}
          minW={{ base: '100%', lg: '0' }}
          gap={{ base: '12px', lg: '16px' }}
          alignItems={{ base: 'stretch', lg: 'center' }}
          flexDir={{ base: 'column', lg: 'row' }}>
          <Text as='h3' fontSize={17} fontWeight={500} display='flex' alignItems='center' color='brand.500' gap='6px' whiteSpace='nowrap'>
            <HiArrowUturnDown />{_isCurrentViewMobile ? 'Route Traffic' : `Route Traffic${_companyName ? ` - ${_companyName}` : ''}`}.
          </Text>
          <DateRangePicker
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
            w={{ base: '100%', lg: '185px' }}
            bg='purple.50'
            color='purple.600'
            fontSize='13px'
            height='max-content'
            gap='6px'
            setSelectDate={setDateRange}
          />
        </Flex>
        <Flex
          flex={{ base: '1', lg: '0 0 auto' }}
          minW='0'
          ml={{ base: 0, lg: 'auto' }}
          justify={{ base: 'flex-start', lg: 'flex-end' }}
          alignItems='flex-end'>
          <Flex
            gap={{ base: '12px', lg: '14px', xl: '16px' }}
            alignItems='center'
            flexDir={{ base: 'column', md: 'row' }}
            flexWrap={{ base: 'wrap', lg: 'nowrap' }}
            justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
            w={{ base: '100%', lg: 'auto' }}>
            <MemoizedSelect
              w={{ base: '100%', md: '240px', lg: '250px', xl: '270px' }}
              minW={{ base: '100%', md: '220px', lg: '220px' }}
              h='40px'
              name='customerAccountId'
              placeholder='All Customer Accounts'
              bg='purple.50'
              color='purple'
              data={customerAccountId}
              options={_customerAccountOptions}
              onChange={e => setCustomerAccountId(e.target?.value)}
            />
            <Flex
              w={{ base: '100%', md: '240px', lg: '250px', xl: '270px' }}
              minW={{ base: '100%', md: '220px', lg: '220px' }}
              h='40px'>
              <MccSelector
                mccValue={destination?.mcc}
                disabled={QueryRouteTrafficResponse?.loading}
                onChange={setDestination}
              />
            </Flex>
            <Button
              borderRadius='12px'
              fontWeight='400'
              h='40px'
              minW={{ base: '100%', md: '116px' }}
              w={{ base: '100%', md: '116px' }}
              bg='purple.50'
              color='purple'
              _hover={{ bg: 'purple.800', color: 'white' }}
              onClick={() => {
                _QueryCustomerAccountRead.refetch({ 'customerId': _customerId })
                setRefreshTrigger(prev => prev + 1)
              }}
              isDisabled={_QueryCustomerAccountRead?.loading || QueryRouteTrafficResponse?.loading || _.isEmpty(_customerId)}
              leftIcon={<HiArrowPath />}>
              Refresh
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex
        position={isEmbedded ? 'relative' : 'sticky'}
        top='0'
        width='100%'
        height={isEmbedded ? 'auto' : 'calc(100vh - 280px)'}
        minHeight={isEmbedded ? '0' : '400px'}
        flex={isEmbedded ? 1 : void 0}
        flexDirection='column'
        sx={{
          '& .MuiDataGrid-root': {
            border: isEmbedded ? 'none' : '1px solid #E2E8F0',
            borderRadius: isEmbedded ? 0 : '12px',
            height: '100%'
          },
          '& .MuiDataGrid-main': {
            overflowY: 'auto'
          }
        }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={58}
          paginationMode='client'
          disableColumnFilter
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[8, 20, 50, 100]}
          slots={{
            'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          loading={QueryRouteTrafficResponse.loading}
        />
      </Flex>
    </>
  )

  // Return component.
  if (isEmbedded) {
    return (
      <Flex
        w='100%'
        h='100%'
        flex={1}
        flexDirection='column'
        gap='0'
        bg='white'
        p='0'>
        {_content}
      </Flex>
    )
  }

  return (
    <Flex className='reportsCustomer base'>
      <Flex
        display='flex'
        flex={1}
        flexDirection='column'
        gap={_isCurrentViewMobile ? '12px' : '22px'}
        bg='white'
        borderRadius='20px'
        p={_isCurrentViewMobile ? '12px' : '22px'}>
        {_content}
      </Flex>
    </Flex>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object,
  'activeCustomer': PropTypes.object,
  'customerOverride': PropTypes.object,
  'isEmbedded': PropTypes.bool
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({
  'passOn': __state.PassOn,
  'activeCustomer': __state.ActiveCustomer
})


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
